<template>
  <b-dropdown
    :id="controlId"
    ref="$dropdown"
    class="select-dropdown"
    variant="light"
    :disabled="isDisabled"
    :toggle-class="toggleClass"
    no-caret
    lazy
    @show="isOpen = true"
    @hide="isOpen = false"
  >
    <template #button-content>
      <div class="d-flex align-items-center justify-content-between w-100">
        <span class="text-truncate" :class="{ 'text-muted': !hasSelection }">{{ toggleLabel }}</span>
        <font-awesome-icon
          :icon="['fal', isOpen ? 'chevron-up' : 'chevron-down']"
          class="dropdown-control__chevron ml-2"
        />
      </div>
    </template>

    <template #default>
      <b-dropdown-form>
        <div class="dropdown-control__panel">
          <div class="dropdown-control__list">
            <template v-for="(option, index) in displayedOptions">
              <!-- A radio next to something nobody can pick offers a choice that is not
                   there, so an unselectable placeholder is plain text. -->
              <div v-if="option.disabled" :key="index" class="dropdown-control__placeholder">
                {{ option.text }}
              </div>
              <div v-else-if="option.header" :key="index" class="select-dropdown__group">
                {{ option.text }}
              </div>
              <b-form-radio
                v-else
                :key="index"
                :checked="checkedValue"
                :value="option.value"
                :name="`${controlId}-option`"
                @change="select(option.value)"
              >
                {{ option.text }}
              </b-form-radio>
            </template>
          </div>
        </div>
      </b-dropdown-form>
    </template>
  </b-dropdown>
</template>

<script>
import { computed, getCurrentInstance, ref } from 'vue';

/**
 * Single-select counterpart to MultiSelect, so a form can hold both without one of
 * them looking like a native control. Forms reach it through AppInput's
 * `type="dropdown"`, because AppInput owns the ValidationProvider that keeps `rules`,
 * `name`, `vid` and the invalid feedback working. Filter bars validate nothing and
 * use it directly.
 *
 * The menu is lazy: some filter bars list every award or division, and the
 * Recognition page mounts all of its tabs at once.
 *
 * Deliberately plain `<script>` + `setup()`: the pinned @vue/vue2-jest cannot compile
 * `<script setup>`, and this component needs unit tests.
 */
// Radios group by `name`, so two unnamed dropdowns on one page would cross-link and
// deselect each other. AppInput derives `id` from `name`, which not every call site sets.
let uid = 0;

// b-form-radio treats an `undefined` prop as absent and falls back to null, so the
// cleared row would never render as checked. Carry it as a sentinel inside the list and
// translate at both edges, leaving the emitted value a real `undefined`.
const NO_VALUE = '__select_dropdown_none__';

export default {
  name: 'SelectDropdown',
  props: {
    id: { type: String, default: undefined },
    // Scalar, unlike MultiSelect's array. undefined means "nothing chosen".
    value: { type: [String, Number, Boolean], default: undefined },
    options: { type: Array, required: true }, // [{ text, value }], optionally in b-form-select's { label, options } groups
    placeholder: { type: String, default: 'Select...' },
    disabled: { type: Boolean, default: false },
    // Mirrors AppInput's `<option :value="undefined" :disabled="!undefinedAllow">`:
    // a row that clears the value, offered only when it is actually selectable.
    undefinedAllow: { type: Boolean, default: false },
    undefinedLabel: { type: String, default: '' },
    // vee-validate tri-state handed down by AppInput: false invalid, true valid, null untouched.
    state: { type: Boolean, default: null },
    // AppInput passes its own `type` through getControlProps. Declared so it does not
    // land on the root element as a stray attribute.
    type: { type: String, default: undefined },
  },
  emits: ['input'],
  setup(props, { emit }) {
    // Vue 2.7 only wires template refs into setup()'s return value for `<script setup>`
    // components; with a plain setup() they land on the instance, so reach them there.
    const instance = getCurrentInstance();
    const isOpen = ref(false);
    const fallbackId = `select-dropdown-${(uid += 1)}`;

    const controlId = computed(() => props.id || fallbackId);

    const isNoOptions = computed(() => !(props.options?.length > 0));
    const isDisabled = computed(() => props.disabled || isNoOptions.value);

    // A native select always renders `<option :value="undefined" :disabled="!undefinedAllow">`
    // (AppInput.vue:31) — the row is there either way, and undefinedAllow only decides
    // whether it can be picked. Mirror that rather than hiding the row, so the panel reads
    // the same as the control it replaced.
    const displayedOptions = computed(() => {
      // b-form-select's optgroup shape: a group becomes a heading row followed by its options.
      const options = (props.options || []).flatMap(option =>
        option.options ? [{ text: option.label, header: true }, ...option.options] : option,
      );
      if (!props.undefinedLabel) {
        return options;
      }
      return [{ text: props.undefinedLabel, value: NO_VALUE, disabled: !props.undefinedAllow }, ...options];
    });

    // An unselectable placeholder has no radio to match, so this only ever resolves against
    // the selectable empty row ("All pay types").
    const checkedValue = computed(() => (props.value === undefined ? NO_VALUE : props.value));

    // A group only heads its rows, so look inside it for the selected option.
    const selectedOption = computed(() =>
      (props.options || []).flatMap(option => option.options || option).find(option => option.value === props.value),
    );

    const hasSelection = computed(() => selectedOption.value !== undefined || props.undefinedAllow);

    // undefinedLabel covers both of AppInput's cases and the toggle shows it either way;
    // hasSelection is what tells them apart. With undefinedAllow the cleared state is a
    // real choice ("All pay types") and reads as a value; without it the label is a
    // placeholder ("Select type") and stays muted.
    const toggleLabel = computed(() => {
      if (selectedOption.value) {
        return selectedOption.value.text;
      }
      return props.undefinedLabel || props.placeholder;
    });

    const toggleClass = computed(() => [
      'dropdown-control__toggle form-control d-flex align-items-center justify-content-between',
      props.state === false ? 'is-invalid' : '',
    ]);

    const select = value => {
      emit('input', value === NO_VALUE ? undefined : value);
      instance.proxy.$refs.$dropdown?.hide(true);
    };

    return {
      isOpen,
      controlId,
      isDisabled,
      displayedOptions,
      checkedValue,
      hasSelection,
      toggleLabel,
      toggleClass,
      select,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components/dropdown-control';

/* Flush with the left edge of the list, where the option indicators start — it has no
   indicator of its own to sit behind. Spacing matches .custom-control's row rhythm. */
.dropdown-control__placeholder {
  color: var(--palette-color-default-lighten-30);
  font-size: 14px;
  line-height: 1.5rem;
  margin-bottom: 0.5rem;
  user-select: none;
}

/* An optgroup label. It sits closer to the rows below than to those above, so it reads
   as heading its own group. */
.select-dropdown__group {
  color: var(--palette-color-default-lighten-30);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5rem;
  margin-bottom: 0.25rem;
  user-select: none;
}

/* Remove default padding from the inner dropdown form */
.select-dropdown :deep(form) {
  padding: 0.7rem 0;
}

.select-dropdown {
  width: 100%;
}

/* Bootstrap sizes .dropdown-menu at a flat 10rem, which wraps "Select period" in the
   narrow budget field. Size to the options instead, never narrower than the toggle and
   never wider than the card. */
.select-dropdown :deep(.dropdown-menu) {
  width: max-content;
  min-width: 100%;
  max-width: 24rem;
}

/* AppInput always wraps the control in a b-input-group. Bootstrap's own
   `.input-group > .form-control` sizing cannot apply, because the element sitting in the
   group is `.dropdown`, not the control — so `width: 100%` above would push the toggle
   onto its own row whenever the group has other children. */
.input-group .select-dropdown {
  flex: 1 1 auto;
  width: 1%;
  min-width: 0;
}

/* Only square the edge that actually butts against a prepend (the budget period's "Per").
   Keyed off the sibling rather than .input-group, which is present on every instance. */
.input-group-prepend + .select-dropdown :deep(.dropdown-control__toggle) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
